import clsx from 'clsx'
import { ComponentPropsWithoutRef, forwardRef, ReactNode } from 'react'

export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
export type ButtonHierarchy = 'primary' | 'secondary' | 'tertiary' | 'link-color' | 'link-gray'
export type ButtonState = 'default' | 'hover' | 'selected' | 'disabled' | 'loading'

type VisualState = 'default' | 'hover' | 'selected' | 'loading'

export interface ButtonProps extends Omit<ComponentPropsWithoutRef<'button'>, 'children'> {
    size?: ButtonSize
    hierarchy?: ButtonHierarchy
    state?: ButtonState
    destructive?: boolean
    iconOnly?: boolean
    underlined?: boolean
    iconLeading?: ReactNode
    iconTrailing?: ReactNode
    loadingText?: ReactNode
    children?: ReactNode
}

const SIZE_CLASSES: Record<ButtonSize, string> = {
    xs: 'h-[30px] px-2.5 gap-spacing-xs text-sm',
    sm: 'h-9 px-spacing-lg gap-spacing-xs text-sm',
    md: 'h-10 px-3.5 gap-spacing-xs text-sm',
    lg: 'h-10 px-spacing-xl gap-spacing-sm text-md',
    xl: 'h-11 px-[18px] gap-spacing-sm text-md',
}

const ICON_ONLY_SIZE_CLASSES: Record<ButtonSize, string> = {
    xs: 'h-8 w-8 p-spacing-md',
    sm: 'h-9 w-9 p-spacing-md',
    md: 'h-10 w-10 p-2.5',
    lg: 'h-11 w-11 p-spacing-lg',
    xl: 'h-12 w-12 p-3.5',
}

const LINK_SIZE_CLASSES: Record<ButtonSize, string> = {
    xs: 'gap-spacing-xs text-sm',
    sm: 'gap-spacing-xs text-sm',
    md: 'gap-spacing-xs text-sm',
    lg: 'gap-spacing-sm text-md',
    xl: 'gap-spacing-sm text-md',
}

const ICON_BOX_CLASSES: Record<ButtonSize, string> = {
    xs: 'h-4 w-4',
    sm: 'h-5 w-5',
    md: 'h-5 w-5',
    lg: 'h-5 w-5',
    xl: 'h-5 w-5',
}

const HIERARCHY_CLASSES: Record<ButtonHierarchy, Record<VisualState, string>> = {
    primary: {
        default:
            'border-2 border-base-transparent bg-background-bg-brand-solid text-text-primary_on-brand shadow-xs-skeuomorphic enabled:hover:bg-background-bg-brand-solid_hover',
        hover: 'border-2 border-base-transparent bg-background-bg-brand-solid_hover text-text-primary_on-brand shadow-xs-skeuomorphic',
        selected:
            'border-2 border-border-brand bg-background-bg-brand-solid text-text-primary_on-brand',
        loading:
            'border-2 border-base-transparent bg-background-bg-brand-solid_hover text-text-primary_on-brand shadow-xs-skeuomorphic',
    },
    secondary: {
        default:
            'border border-border-primary bg-background-bg-primary text-text-secondary shadow-xs-skeuomorphic enabled:hover:bg-background-bg-primary_hover enabled:hover:text-text-secondary_hover',
        hover: 'border border-border-primary bg-background-bg-primary_hover text-text-secondary_hover shadow-xs-skeuomorphic',
        selected: 'border border-border-brand bg-background-bg-primary text-text-secondary',
        loading:
            'border border-border-primary bg-background-bg-primary_hover text-text-secondary shadow-xs-skeuomorphic',
    },
    tertiary: {
        default:
            'border border-base-transparent bg-base-transparent text-text-tertiary enabled:hover:bg-background-bg-primary_hover enabled:hover:text-text-tertiary_hover',
        hover: 'border border-base-transparent bg-background-bg-primary_hover text-text-tertiary_hover',
        selected:
            'border border-base-transparent bg-background-bg-primary text-text-brand-tertiary',
        loading: 'border border-base-transparent bg-background-bg-primary_hover text-text-tertiary',
    },
    'link-color': {
        default:
            'text-text-brand-secondary enabled:hover:text-text-brand-secondary_hover enabled:hover:underline',
        hover: 'text-text-brand-secondary_hover underline',
        selected: 'rounded-radius-xs text-text-brand-secondary',
        loading: 'text-text-brand-secondary',
    },
    'link-gray': {
        default:
            'text-text-tertiary enabled:hover:text-text-tertiary_hover enabled:hover:underline',
        hover: 'text-text-tertiary_hover underline',
        selected: 'rounded-radius-xs text-text-tertiary',
        loading: 'text-text-tertiary',
    },
}

const DESTRUCTIVE_HIERARCHY_CLASSES: Record<ButtonHierarchy, Record<VisualState, string>> = {
    primary: {
        default:
            'border-2 border-base-transparent bg-background-bg-error-solid text-text-white shadow-xs-skeuomorphic enabled:hover:bg-background-bg-error-solid_hover',
        hover: 'border-2 border-base-transparent bg-background-bg-error-solid_hover text-text-white shadow-xs-skeuomorphic',
        selected: 'border-2 border-border-error bg-background-bg-error-solid text-text-white',
        loading:
            'border-2 border-base-transparent bg-background-bg-error-solid_hover text-text-white shadow-xs-skeuomorphic',
    },
    secondary: {
        default:
            'border border-border-error-subtle bg-background-bg-primary text-text-error shadow-xs-skeuomorphic enabled:hover:bg-background-bg-error-primary enabled:hover:text-text-error_hover',
        hover: 'border border-border-error-subtle bg-background-bg-error-primary text-text-error_hover shadow-xs-skeuomorphic',
        selected: 'border border-border-error bg-background-bg-primary text-text-error',
        loading:
            'border border-border-error-subtle bg-background-bg-error-primary text-text-error shadow-xs-skeuomorphic',
    },
    tertiary: {
        default:
            'border border-base-transparent bg-base-transparent text-text-error enabled:hover:bg-background-bg-error-primary enabled:hover:text-text-error_hover',
        hover: 'border border-base-transparent bg-background-bg-error-primary text-text-error_hover',
        selected: 'border border-base-transparent bg-background-bg-primary text-text-error',
        loading: 'border border-base-transparent bg-background-bg-error-primary text-text-error',
    },
    'link-color': {
        default: 'text-text-error enabled:hover:text-text-error_hover enabled:hover:underline',
        hover: 'text-text-error_hover underline',
        selected: 'rounded-radius-xs text-text-error',
        loading: 'text-text-error',
    },
    'link-gray': {
        default: 'text-text-error enabled:hover:text-text-error_hover enabled:hover:underline',
        hover: 'text-text-error_hover underline',
        selected: 'rounded-radius-xs text-text-error',
        loading: 'text-text-error',
    },
}

const ICON_COLOR_CLASSES: Record<ButtonHierarchy, Record<VisualState, string>> = {
    primary: {
        default: 'text-foreground-fg-white',
        hover: 'text-foreground-fg-white',
        selected: 'text-foreground-fg-white',
        loading: 'text-foreground-fg-white',
    },
    secondary: {
        default: 'text-foreground-fg-quaternary',
        hover: 'text-foreground-fg-quaternary_hover',
        selected: 'text-foreground-fg-quaternary',
        loading: 'text-foreground-fg-quaternary',
    },
    tertiary: {
        default: 'text-foreground-fg-quaternary',
        hover: 'text-foreground-fg-quaternary_hover',
        selected: 'text-foreground-fg-brand-tertiary',
        loading: 'text-foreground-fg-quaternary',
    },
    'link-color': {
        default: 'text-foreground-fg-brand-secondary',
        hover: 'text-foreground-fg-brand-secondary',
        selected: 'text-foreground-fg-brand-secondary',
        loading: 'text-foreground-fg-brand-secondary',
    },
    'link-gray': {
        default: 'text-foreground-fg-tertiary',
        hover: 'text-foreground-fg-tertiary_hover',
        selected: 'text-foreground-fg-tertiary',
        loading: 'text-foreground-fg-tertiary',
    },
}

const toVisualState = (state: ButtonState): VisualState => {
    switch (state) {
        case 'hover':
            return 'hover'
        case 'selected':
            return 'selected'
        case 'loading':
            return 'loading'
        default:
            return 'default'
    }
}

const LoadingSpinner = ({ className }: { className: string }) => (
    <svg
        className={clsx('animate-spin', className)}
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true">
        <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="3"
        />
        <path
            stroke="currentColor"
            strokeWidth="3"
            strokeLinecap="round"
            d="M12 2a10 10 0 0 1 10 10"
        />
    </svg>
)

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            size = 'md',
            hierarchy = 'primary',
            state = 'default',
            destructive = false,
            iconOnly = false,
            underlined = false,
            iconLeading,
            iconTrailing,
            loadingText,
            className,
            disabled,
            children,
            ...props
        },
        ref,
    ) => {
        const isDisabled = Boolean(disabled) || state === 'disabled'
        const isLoading = state === 'loading'
        const isLink = hierarchy === 'link-color' || hierarchy === 'link-gray'
        const isIconOnly = iconOnly && !isLink
        const visualState = toVisualState(state)

        if (process.env.NODE_ENV === 'development' && isIconOnly) {
            if (!props['aria-label'] && !props['aria-labelledby']) {
                console.error('Button: iconOnly={true} requires aria-label or aria-labelledby')
            }
        }

        const hierarchyClasses = destructive
            ? DESTRUCTIVE_HIERARCHY_CLASSES[hierarchy][visualState]
            : HIERARCHY_CLASSES[hierarchy][visualState]

        const iconColorClasses = destructive ? '' : ICON_COLOR_CLASSES[hierarchy][visualState]
        const iconBoxClasses = ICON_BOX_CLASSES[size]

        let sizeClasses = SIZE_CLASSES[size]
        if (isLink) {
            sizeClasses = LINK_SIZE_CLASSES[size]
        } else if (isIconOnly) {
            sizeClasses = ICON_ONLY_SIZE_CLASSES[size]
        }

        const label = isLoading && loadingText !== undefined ? loadingText : children
        const showLabel = !isIconOnly && label !== undefined && label !== null && label !== false

        const leading = isLoading ? (
            <LoadingSpinner className={iconBoxClasses} />
        ) : (
            iconLeading && (
                <span
                    aria-hidden="true"
                    className={clsx(
                        'inline-flex shrink-0 items-center justify-center',
                        iconBoxClasses,
                        iconColorClasses,
                    )}>
                    {iconLeading}
                </span>
            )
        )

        const trailing = !isLoading && iconTrailing && (
            <span
                aria-hidden="true"
                className={clsx(
                    'inline-flex shrink-0 items-center justify-center',
                    iconBoxClasses,
                    iconColorClasses,
                )}>
                {iconTrailing}
            </span>
        )

        return (
            <button
                {...props}
                ref={ref}
                disabled={isDisabled || isLoading}
                aria-busy={isLoading || undefined}
                className={clsx(
                    'inline-flex items-center justify-center whitespace-nowrap',
                    'font-display font-semibold',
                    'transition-colors duration-100',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                    destructive
                        ? 'focus-visible:ring-focus-ring-error'
                        : 'focus-visible:ring-focus-ring',
                    isLink ? 'rounded-radius-xs' : 'rounded-radius-md',
                    underlined && isLink && 'underline',
                    isDisabled && 'cursor-not-allowed opacity-50',
                    isLoading && 'cursor-wait',
                    sizeClasses,
                    hierarchyClasses,
                    className,
                )}>
                {leading}
                {showLabel && <span className={clsx(!isLink && 'px-spacing-xxs')}>{label}</span>}
                {!isIconOnly && trailing}
                {isIconOnly && !isLoading && !iconLeading && trailing}
            </button>
        )
    },
)

Button.displayName = 'Button'
